import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  FormModal,
  Input,
  Page,
  Select,
  Table,
  Text,
  Textarea,
  type Column,
} from "@marekl/adminer/components";
import { useTableSort } from "@marekl/adminer/hooks";
import { useSearchParams } from "react-router-dom";
import { AppTopbar } from "../../../shell/AppTopbar";
import { BadgeRow } from "../../../components/BadgeRow";
import { useAdminSeasons } from "../../../lib/queries/seasons";
import { RowLink } from "../../../components/RowLink";
import {
  useCreateRuleSet,
  useDuplicateRuleSet,
  useRuleSets,
  type RuleSetSummary,
} from "../../../lib/queries/rules";
import {
  RULESET_STATUS_LABELS,
  RULESET_STATUS_VARIANT,
} from "../../../lib/rulesLabels";
import { readsRuleSetsOnly, useMe } from "../../../auth/queries";
import type { ID } from "../../../lib/id";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "duplicate"; ruleSet: RuleSetSummary };

interface FormState {
  name: string;
  description: string;
  seasonId: string;
}

const emptyForm: FormState = { name: "", description: "", seasonId: "" };

export function RuleSetsPage() {
  const { data: me } = useMe();
  // Čtení soutěží reads the rule sets and writes none: no new set, no copy.
  const viewOnly = readsRuleSetsOnly(me);
  const { data: ruleSets = [], isLoading } = useRuleSets();
  const { data: seasons = [] } = useAdminSeasons();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState<FormState>(emptyForm);

  const create = useCreateRuleSet();
  const duplicate = useDuplicateRuleSet(modal.kind === "duplicate" ? modal.ruleSet.id : "");
  // Destructured because useMutation returns a NEW object every render
  // ({ ...result, mutate, mutateAsync }), so depending on the mutation
  // itself would rebuild the callback — and with it the memoised columns —
  // on every render, which is exactly what the memo is for. `reset` is
  // bound once in the observer's constructor, so it IS stable.
  const { reset: resetDuplicate } = duplicate;

  const isWriting = create.isPending || duplicate.isPending;
  const writeError = create.error || duplicate.error;

  const close = () => {
    setModal({ kind: "closed" });
    setForm(emptyForm);
    create.reset();
    duplicate.reset();
  };

  const openCreate = () => {
    setForm(emptyForm);
    create.reset();
    setModal({ kind: "create" });
  };

  // useCallback because the columns are memoised for useTableSort.
  const openDuplicate = useCallback(
    (ruleSet: RuleSetSummary) => {
      setForm({
        name: `${ruleSet.name} (kopie)`,
        description: "",
        seasonId: ruleSet.season_id ? String(ruleSet.season_id) : "",
      });
      resetDuplicate();
      setModal({ kind: "duplicate", ruleSet });
    },
    [resetDuplicate],
  );

  const submit = () => {
    const seasonId = form.seasonId ? form.seasonId : null;
    if (modal.kind === "create") {
      create.mutate(
        { name: form.name.trim(), description: form.description.trim(), season_id: seasonId },
        { onSuccess: close },
      );
    } else if (modal.kind === "duplicate") {
      duplicate.mutate({ name: form.name.trim(), season_id: seasonId }, { onSuccess: close });
    }
  };

  const seasonCode = useCallback(
    (id?: ID | null) => seasons.find((s) => s.id === id)?.code ?? "—",
    [seasons],
  );

  // Hoisted for useTableSort, which re-sorts on the columns identity.
  const columns = useMemo<Column<RuleSetSummary>[]>(
    () => [
            {
              key: "name",
              header: "Název",
              sortable: true,
              sortValue: (r) => r.name,
              render: (r) => (
                <>
                  <div>
                    <RowLink to={`/admin/pravidla/${r.id}`}>{r.name}</RowLink>
                  </div>
                  {r.description && (
                    <Text variant="muted">{r.description}</Text>
                  )}
                </>
              ),
            },
            {
              key: "season",
              header: "Sezóna",
              sortable: true,
              // The code, not the id: "2026/2027" is what the cell shows and
              // what sorts chronologically. A set with no season sorts last.
              sortValue: (r) => (r.season_id ? seasonCode(r.season_id) : null),
              render: (r) => <Text variant="muted">{seasonCode(r.season_id)}</Text>,
            },
            {
              key: "status",
              header: "Stav",
              sortable: true,
              sortValue: (r) => RULESET_STATUS_LABELS[r.status],
              render: (r) => (
                <BadgeRow>
                  <Badge variant={RULESET_STATUS_VARIANT[r.status]}>
                    {RULESET_STATUS_LABELS[r.status]}
                  </Badge>
                  {/* „Aktivní" says the set may be used; this says it IS,
                      which is the difference between a set you can still
                      restructure and one whose structure is settled. Worth
                      knowing before opening it, not after the first refused
                      delete. Archived is the broader freeze and says so
                      already. */}
                  {r.in_service && r.status !== "archived" && (
                    <Badge variant="info">V provozu</Badge>
                  )}
                </BadgeRow>
              ),
            },
            {
              key: "disciplines",
              header: "Disciplín",
              numeric: true,
              sortable: true,
              // numeric only picks the first click (desc); the comparator
              // comes from the value's runtime type.
              sortValue: (r) => r.discipline_count,
              render: (r) => r.discipline_count,
            },
            {
              key: "actions",
              header: "",
              render: (r) => (
                <Table.Actions>
                  <Table.Action appearance="button" to={`/admin/pravidla/${r.id}`}>Detail</Table.Action>
                  {!viewOnly && (
                    <Table.Action appearance="button" onClick={() => openDuplicate(r)}>Duplikovat</Table.Action>
                  )}
                </Table.Actions>
              ),
            },
    ],
    [seasonCode, openDuplicate, viewOnly],
  );

  const tableSort = useTableSort(ruleSets, {
    columns,
    searchParams,
    setSearchParams,
    defaultSort: { key: "name", dir: "asc" },
  });

  return (
    <>
      <AppTopbar crumbs={[{ label: "Sady pravidel" }]} />
      <Page>
        <Page.Header
          title="Sady pravidel"
          subtitle="sady soutěžních pravidel, disciplíny a jejich kombinace"
          actions={
            !viewOnly && (
              <Button variant="primary" onClick={openCreate}>
                Nová sada
              </Button>
            )
          }
        />
        <Page.Content stack gap="lg">
          <Table<RuleSetSummary>
            framed
            {...tableSort.tableProps}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            columns={columns}
            empty={
              <EmptyState
                title="Žádné sady pravidel"
                description="Založte první sadu, nebo duplikujte sadu z minulé sezóny."
              />
            }
          />
        </Page.Content>
      </Page>

      <FormModal
        open={modal.kind !== "closed"}
        title={modal.kind === "duplicate" ? "Duplikovat sadu pravidel" : "Nová sada pravidel"}
        submitLabel={modal.kind === "duplicate" ? "Duplikovat" : "Vytvořit"}
        busy={isWriting}
        canSubmit={!!form.name.trim()}
        error={writeError}
        onSubmit={submit}
        onClose={close}
        width="lg"
      >
        {modal.kind === "duplicate" && (
          <Alert variant="info">
            Vytvoří se nezávislá kopie sady <strong>{modal.ruleSet.name}</strong> včetně
            všech tabulek, disciplín a jejich kombinací. Kopie vznikne jako rozpracovaná;
            původní sada zůstane beze změny.
          </Alert>
        )}
        <Input
          label="Název sady"
          required
          placeholder="PRAVIDLA MČR ČNSMS 2027"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        {modal.kind === "create" && (
          <Textarea
            label="Popis"
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            hint="Nepovinné. K čemu se sada používá."
          />
        )}
        <Select
          label="Domovská sezóna"
          value={form.seasonId}
          onChange={(e) => setForm({ ...form, seasonId: e.target.value })}
        >
          <option value="">— bez sezóny —</option>
          {seasons.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code}
            </option>
          ))}
        </Select>
      </FormModal>
    </>
  );
}
